(() => {
  if (window.LED500Checkout) return;

  const state = window.LED500Checkout = {
    version: '2026-09-29.6.4',
    refreshRequired: false,
    active: false,
    pending: new Set(),
    track(promise) {
      this.pending.add(promise);
      const remove = () => this.pending.delete(promise);
      promise.then(remove, remove);
      return promise;
    }
  };
  let activeAttempt = null;
  const integer = (value) => Number.isSafeInteger(Number(value)) && Number(value) >= 0;
  const text = (value) => String(value ?? '');
  const groupKey = (id, product) => JSON.stringify([id, product]);

  function cartItemsFor(element) {
    if (!element) return null;
    return element.closest('cart-items') || element.form?.querySelector('cart-items') ||
      element.closest('main-cart, cart-drawer')?.querySelector('cart-items') ||
      (element.matches('form') ? element.querySelector('cart-items') : null);
  }

  // A complete DOM snapshot is required before taking ownership of checkout.
  function eligible(items) {
    if (!items?.hasAttribute('data-led-raw-item-count')) return null;
    const context = items.closest('main-cart, cart-drawer') || items;
    const controls = new Set(context.querySelectorAll('[name^="attributes["], [required]'));
    const forms = new Set([items.closest('form')]);
    context.querySelectorAll('button[name="checkout"]').forEach(button => forms.add(button.form));
    for (const form of forms) {
      Array.from(form?.elements || []).forEach(control => controls.add(control));
    }
    // Custom cart attributes and required terms keep their native/app workflow.
    if (Array.from(controls).some(control => control.name?.startsWith('attributes[') ||
        (control.required && !control.matches('input[data-index][name="updates[]"]')))) return null;
    const rows = Array.from(items.querySelectorAll('[data-led-line-key]'));
    if (!rows.length) return null;
    const keys = new Set();
    const groups = new Map();
    let quantity = 0;
    const lines = [];
    for (const row of rows) {
      const data = row.dataset;
      const sign = data.ledSign === 'true';
      const fee = data.ledTextFee === 'true';
      const input = row.querySelector('input[data-index]');
      if (sign === fee || data.ledExternalOptions === 'true' || !input || !data.ledLineKey || keys.has(data.ledLineKey) ||
          input.dataset.index !== data.ledLineKey || !integer(data.ledLineQuantity) ||
          Number(data.ledLineQuantity) < 1 || !/^\d+$/.test(data.ledProductId || '') ||
          !/^\d+$/.test(data.ledVariantId || '')) return null;
      keys.add(data.ledLineKey);
      quantity += Number(data.ledLineQuantity);
      const line = {
        key: data.ledLineKey, product: data.ledProductId, variant: data.ledVariantId,
        customization: data.ledCustomizationId || '', parent: data.ledParentProductId || '',
        customText: data.ledCustomText || '', sign, fee, input, row
      };
      if (sign && line.parent !== line.product) return null;
      if (fee || line.customization) {
        if (!line.customization || !line.parent) return null;
        const key = groupKey(line.customization, line.parent);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(line);
      }
      lines.push(line);
    }
    if (quantity !== Number(items.dataset.ledRawItemCount)) return null;
    for (const line of lines) {
      const group = groups.get(groupKey(line.customization, line.parent)) || [];
      if (line.fee || line.customText.trim() || line.row.dataset.ledFeeGrouped === 'true') {
        if (group.length !== 2 || group.filter(item => item.sign).length !== 1 ||
            group.filter(item => item.fee).length !== 1 ||
            group.some(item => item.row.dataset.ledFeeGrouped !== 'true')) return null;
      } else if (group.length > 1) return null;
    }
    return { items, lines, groups };
  }

  // Capture the current field immediately before a section replacement; there
  // is no persistent draft that could overwrite a later saved or cleared note.
  state.captureNote = (host) => {
    const items = host?.querySelector('cart-items');
    const note = host?.querySelector('cart-note [name="note"]');
    return note && eligible(items) ? { value: note.value } : null;
  };
  state.restoreNote = (host, snapshot) => {
    if (!snapshot) return;
    const note = host?.querySelector('cart-note [name="note"]');
    if (note) note.value = snapshot.value;
  };

  function message(snapshot, kind) {
    return snapshot.items.dataset[kind] || 'We could not open checkout. Please try again.';
  }

  function showError(attempt, content) {
    let error = attempt.error;
    if (!error?.isConnected) {
      error = document.createElement('p');
      error.dataset.ledCheckoutErrorMessage = '';
      error.className = 'text-sm leading-normal';
      error.style.color = '#b42318';
      error.setAttribute('role', 'alert');
      error.setAttribute('aria-live', 'assertive');
      const anchor = attempt.button.isConnected ? attempt.button :
        attempt.host?.querySelector('button[name="checkout"]') || attempt.host || attempt.snapshot.items;
      if (anchor?.isConnected) anchor.insertAdjacentElement('afterend', error);
      attempt.error = error;
    }
    error.textContent = content;
  }

  function editorPreviewUrl(snapshot) {
    const data = snapshot.items.dataset;
    const id = data.ledPreviewThemeId || '';
    if (!/^[1-9]\d*$/.test(id) || !data.ledPreviewCartUrl) return null;
    try {
      const url = new URL(data.ledPreviewCartUrl, location.origin);
      const cartUrl = new URL(theme.routes.cart_url, location.origin);
      if (url.origin !== location.origin || cartUrl.origin !== location.origin ||
          url.username || url.password || url.hash || url.pathname !== cartUrl.pathname ||
          url.searchParams.get('preview_theme_id') !== id ||
          Array.from(url.searchParams).length !== 1) return null;
      return url.href;
    } catch (_) {
      return null;
    }
  }

  function showEditorCheckout(snapshot, button) {
    const host = button.closest('main-cart, cart-drawer') || snapshot.items;
    host.querySelectorAll('[data-led-checkout-error-message]').forEach(node => node.remove());
    let notice = host.querySelector('[data-led-editor-checkout-notice]');
    if (!notice) {
      notice = document.createElement('div');
      notice.dataset.ledEditorCheckoutNotice = '';
      notice.className = 'col-span-full w-full text-sm leading-normal';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      button.insertAdjacentElement('afterend', notice);
    }
    notice.textContent = '';
    const url = editorPreviewUrl(snapshot);
    const explanation = document.createElement('p');
    explanation.textContent = url ? snapshot.items.dataset.ledEditorCheckoutMessage ||
      'Test checkout outside the theme editor. Open the draft cart in a new tab, then select Check out.' :
      snapshot.items.dataset.ledEditorCheckoutUnavailable ||
      'Open this theme’s preview from Online Store > Themes to test checkout in a new tab.';
    notice.appendChild(explanation);
    if (!url) return;
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener');
    link.className = 'button button--secondary w-full mt-3';
    const label = document.createElement('span');
    label.className = 'btn-text';
    label.textContent = snapshot.items.dataset.ledEditorCheckoutLink || 'Open draft cart in a new tab';
    link.appendChild(label);
    notice.appendChild(link);
  }

  function restore(attempt) {
    if (activeAttempt !== attempt) return;
    clearTimeout(attempt.watchdog);
    attempt.observer?.disconnect();
    attempt.controller.abort();
    state.active = false;
    for (const [control, previous] of attempt.controls || []) {
      control.disabled = previous.disabled;
      if ('readOnly' in control) control.readOnly = previous.readOnly;
      if (control.matches('button[name="checkout"]')) control.removeAttribute('aria-busy');
    }
    attempt.button.disabled = attempt.disabled;
    attempt.button.removeAttribute('aria-busy');
    activeAttempt = null;
  }

  function fail(attempt, kind) {
    if (activeAttempt !== attempt) return;
    showError(attempt, message(attempt.snapshot, kind));
    restore(attempt);
  }

  function snapshotValues(snapshot, button) {
    const updates = {};
    for (const line of snapshot.lines.filter(item => item.sign)) {
      const input = line.input;
      const value = Number(input.value);
      const minimum = Number(input.dataset.min || input.min || 0);
      const maximum = input.max === '' ? Infinity : Number(input.max);
      const step = Number(input.step || 1);
      const stepAnchor = input.min === '' ? 0 : Number(input.min);
      if (input.value.trim() === '' || !integer(input.value) || value > maximum ||
          (value !== 0 && (value < minimum || (step > 0 && (value - stepAnchor) % step !== 0)))) {
        throw new Error('ledCheckoutQuantity');
      }
      updates[line.key] = value;
      const group = snapshot.groups.get(groupKey(line.customization, line.parent)) || [];
      for (const fee of group.filter(item => item.fee)) updates[fee.key] = value;
    }
    const context = button.closest('main-cart, cart-drawer');
    const note = Array.from(button.form?.elements || []).find(element => element.name === 'note') ||
      context?.querySelector('cart-note [name="note"]');
    return { updates, ...(note ? { note: note.value } : {}) };
  }

  function freezeControls(attempt) {
    if (activeAttempt !== attempt) return;
    const controls = new Set(attempt.snapshot.lines.map(line => line.input));
    controls.add(attempt.button);
    attempt.host?.querySelectorAll('cart-items input[data-index], cart-note [name="note"], button[name="checkout"]')
      .forEach(control => controls.add(control));
    for (const control of controls) {
      if (!attempt.controls.has(control)) {
        attempt.controls.set(control, { disabled: control.disabled, readOnly: control.readOnly });
      }
      if (control.name === 'note') control.readOnly = true;
      else control.disabled = true;
      if (control.matches('button[name="checkout"]') && control.getAttribute('aria-busy') !== 'true') {
        control.setAttribute('aria-busy', 'true');
      }
    }
  }

  function verifyCart(cart, snapshot, updates = null, privateEngineering = false) {
    if (!Array.isArray(cart.items) || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0) {
      throw new Error('ledCheckoutChanged');
    }
    const expected = new Map(snapshot.lines.filter(line => !updates || updates[line.key] > 0)
      .map(line => [line.key, line]));
    if (cart.items.length !== expected.size) throw new Error('ledCheckoutChanged');
    const seen = new Set();
    for (const item of cart.items) {
      const line = expected.get(item.key);
      const properties = item.properties || {};
      if (privateEngineering && privateEngineeringFields.some(key =>
          Object.prototype.hasOwnProperty.call(properties, key))) throw new Error('ledCheckoutChanged');
      if (!line || seen.has(item.key) || text(item.product_id) !== line.product ||
          text(item.variant_id) !== line.variant || !integer(item.quantity) || item.quantity < 1 ||
          (updates && item.quantity !== updates[line.key]) ||
          Object.keys(properties).some(key => /^(?:_?ymq|_?bold|_?gpo|_?tpo|_?hulk|_?infiniteoptions|_?option_price)/i.test(key))) {
        throw new Error('ledCheckoutChanged');
      }
      seen.add(item.key);
      if (text(properties['_Customization ID']) !== line.customization ||
          (line.fee && (text(properties['_LED Text Fee']) !== 'true' ||
            text(properties['_Parent Product ID']) !== line.parent))) throw new Error('ledCheckoutChanged');
      if (line.sign) {
        const customText = properties['Custom text'] || properties['Bottom Text'] || properties['_Bottom Text'] || '';
        if (text(customText) !== line.customText) throw new Error('ledCheckoutChanged');
      }
    }
  }

  const privateEngineeringFields = [
    'Bottom Text Font', 'Bottom Text Character Count', 'Bottom Text Character Width',
    'Bottom Text Character Height', 'Bottom Text Layout', 'Bottom Text Construction',
    'Backboard Construction', 'Original Sign Size'
  ];
  const propertySignature = properties => JSON.stringify(Object.keys(properties || {}).sort()
    .map(key => [key, properties[key]]));
  const lineSignature = (item, properties = item.properties || {}) => JSON.stringify([
    text(item.product_id), text(item.variant_id), text(properties['_Customization ID']),
    text(properties['_LED Text Fee']), text(properties['_Parent Product ID']),
    text(item.selling_plan_allocation?.selling_plan?.id), propertySignature(properties)
  ]);

  function privateProperties(properties) {
    const result = { ...properties };
    for (const key of privateEngineeringFields) {
      if (!Object.prototype.hasOwnProperty.call(result, key)) continue;
      const privateKey = `_${key}`;
      if (Object.prototype.hasOwnProperty.call(result, privateKey) &&
          JSON.stringify(result[privateKey]) !== JSON.stringify(result[key])) {
        throw new Error('ledCheckoutChanged');
      }
      result[privateKey] = result[key];
      delete result[key];
    }
    return result;
  }

  function migrationPlan(cart) {
    const candidates = cart.items.map(item => {
      const properties = privateProperties(item.properties || {});
      return { key: item.key, properties, signature: lineSignature(item, properties),
        changed: propertySignature(properties) !== propertySignature(item.properties) };
    });
    if (!candidates.some(item => item.changed)) return [];
    const seen = new Set();
    for (const item of candidates) {
      if (seen.has(item.signature)) throw new Error('ledCheckoutChanged');
      seen.add(item.signature);
    }
    return candidates.filter(item => item.changed);
  }

  function reconcileMigration(before, after, changedKey, properties, snapshot, updates) {
    const cartFields = ['total_price', 'original_total_price', 'total_discount',
      'items_subtotal_price', 'item_count', 'currency'];
    if (!Array.isArray(after.items) || after.items.length !== before.items.length ||
        cartFields.some(key => after[key] !== before[key])) throw new Error('ledCheckoutChanged');
    const moneyFields = ['price', 'original_price', 'final_price', 'discounted_price',
      'line_price', 'original_line_price', 'final_line_price', 'total_discount'];
    const keyMap = new Map();
    const matched = new Set();
    for (const previous of before.items) {
      const expectedProperties = previous.key === changedKey ? properties : previous.properties || {};
      const signature = lineSignature(previous, expectedProperties);
      const candidates = after.items.filter(item => lineSignature(item) === signature);
      if (candidates.length !== 1) throw new Error('ledCheckoutChanged');
      const current = candidates[0];
      if (!current.key || matched.has(current.key) || current.quantity !== previous.quantity ||
          moneyFields.some(key => current[key] !== previous[key])) throw new Error('ledCheckoutChanged');
      matched.add(current.key);
      keyMap.set(previous.key, current.key);
    }
    for (const previous of before.items) {
      const current = after.items.find(item => item.key === keyMap.get(previous.key));
      const previousParent = previous.parent_relationship?.parent_key;
      const currentParent = current.parent_relationship?.parent_key;
      if (currentParent !== (previousParent ? keyMap.get(previousParent) : previousParent)) {
        throw new Error('ledCheckoutChanged');
      }
    }
    const remapped = {};
    for (const line of snapshot.lines) {
      const key = keyMap.get(line.key);
      if (!key || !Object.prototype.hasOwnProperty.call(updates, line.key)) {
        throw new Error('ledCheckoutChanged');
      }
      remapped[key] = updates[line.key];
    }
    for (const line of snapshot.lines) line.key = keyMap.get(line.key);
    for (const key of Object.keys(updates)) delete updates[key];
    Object.assign(updates, remapped);
    verifyCart(after, snapshot);
    return keyMap;
  }

  async function migrateProperties(cart, snapshot, values, request, changeUrl, attempt) {
    const plan = migrationPlan(cart);
    for (const step of plan) {
      const item = cart.items.find(line => line.key === step.key);
      if (!item) throw new Error('ledCheckoutChanged');
      attempt.migrationStarted = true;
      const updated = await request(changeUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: step.properties })
      });
      const keys = reconcileMigration(cart, updated, item.key, step.properties, snapshot, values.updates);
      for (const pending of plan) pending.key = keys.get(pending.key) || pending.key;
      cart = updated;
    }
    return cart;
  }

  async function checkout(snapshot, button) {
    const attempt = {
      snapshot, button, disabled: button.disabled,
      host: button.closest('main-cart, cart-drawer'), controller: new AbortController(), controls: new Map()
    };
    activeAttempt = attempt;
    state.active = true;
    document.querySelectorAll('[data-led-checkout-error-message]').forEach(node => node.remove());
    try {
      const values = snapshotValues(snapshot, button);
      freezeControls(attempt);
      if (attempt.host) {
        attempt.observer = new MutationObserver(() => freezeControls(attempt));
        attempt.observer.observe(attempt.host, { childList: true, subtree: true });
      }
      const root = theme.routes.root_url || '/';
      const checkoutUrl = new URL(`${root.replace(/\/?$/, '/')}checkout`, location.origin);
      const cartUrl = new URL(`${theme.routes.cart_url}.js`, location.origin);
      const updateUrl = new URL(theme.routes.cart_update_url, location.origin);
      const changeUrl = new URL(theme.routes.cart_change_url, location.origin);
      if ([checkoutUrl, cartUrl, updateUrl, changeUrl].some(url => url.origin !== location.origin)) throw new Error('ledCheckoutError');
      const request = async (url, options = {}) => {
        const checkAttempt = () => {
          if (activeAttempt !== attempt || attempt.controller.signal.aborted) throw new Error('ledCheckoutError');
        };
        checkAttempt();
        const response = await fetch(url, {
          credentials: 'same-origin', cache: 'no-store', ...options,
          headers: { Accept: 'application/json', ...(options.headers || {}) },
          signal: attempt.controller.signal
        });
        checkAttempt();
        if (!response.ok) throw new Error('ledCheckoutError');
        const result = await response.json();
        checkAttempt();
        return result;
      };
      let timer;
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => {
          attempt.controller.abort();
          reject(new Error('ledCheckoutError'));
        }, 12000);
      });
      try {
        await Promise.race([timeout, (async () => {
          await Promise.all(Array.from(state.pending));
          if (attempt.controller.signal.aborted) throw new Error('ledCheckoutError');
          const current = await request(cartUrl);
          verifyCart(current, snapshot);
          await migrateProperties(current, snapshot, values, request, changeUrl, attempt);
          const updated = await request(updateUrl, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(values)
          });
          verifyCart(updated, snapshot, values.updates, true);
          if (!updated.items.length) throw new Error('ledCheckoutEmpty');
          if ('note' in values && text(updated.note) !== values.note) throw new Error('ledCheckoutChanged');
        })()]);
      } finally {
        clearTimeout(timer);
      }
      if (activeAttempt !== attempt || attempt.controller.signal.aborted) return;
      // Keep late debounced updates blocked until navigation or pageshow.
      attempt.watchdog = setTimeout(() => {
        if (attempt.migrationStarted) state.refreshRequired = true;
        fail(attempt, state.refreshRequired ? 'ledCheckoutChanged' : 'ledCheckoutError');
      }, 12000);
      location.assign(checkoutUrl.href);
    } catch (error) {
      const allowed = ['ledCheckoutError', 'ledCheckoutChanged', 'ledCheckoutQuantity', 'ledCheckoutEmpty'];
      if (attempt.migrationStarted) state.refreshRequired = true;
      fail(attempt, state.refreshRequired ? 'ledCheckoutChanged' :
        allowed.includes(error.message) ? error.message : 'ledCheckoutError');
    }
  }

  function start(event, button) {
    if (!button) return;
    if (state.refreshRequired) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const items = cartItemsFor(button);
      const host = button.closest('main-cart, cart-drawer');
      if (items) showError({ button, host, error: host?.querySelector('[data-led-checkout-error-message]'),
        snapshot: { items } }, message({ items }, 'ledCheckoutChanged'));
      return;
    }
    if (state.active) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const snapshot = eligible(cartItemsFor(button));
    if (!snapshot) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (snapshot.items.dataset.ledDesignMode === 'true' || window.Shopify?.designMode === true) {
      showEditorCheckout(snapshot, button);
      return;
    }
    void checkout(snapshot, button);
  }

  document.addEventListener('click', event => {
    if (state.refreshRequired && event.target.closest?.('cart-items') &&
        event.target.closest?.('button, cart-remove-button, [is="cart-remove-button"]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const button = event.target.closest?.('button[name="checkout"]');
    start(event, button);
  }, true);

  document.addEventListener('submit', event => {
    const button = event.submitter || Array.from(event.target.elements || [])
      .find(element => element.name === 'checkout');
    if (button?.name === 'checkout') start(event, button);
  }, true);

  document.addEventListener('change', event => {
    const input = event.target;
    if (!input.matches?.('input[data-index][name="updates[]"]')) return;
    if (state.refreshRequired) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const items = input.closest('cart-items');
    if (!eligible(items) || typeof items.onChangeListener !== 'function') return;
    // The options app otherwise schedules a delayed full-page reload here.
    event.stopImmediatePropagation();
    if (state.active) return;
    input.closest('quantity-input')?.onInputChange?.();
    items.onChangeListener(event);
  }, true);

  window.addEventListener('pageshow', () => {
    if (activeAttempt?.migrationStarted) {
      state.refreshRequired = true;
      fail(activeAttempt, 'ledCheckoutChanged');
    } else if (activeAttempt) restore(activeAttempt);
  });
})();
